import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faCrown, faStar, faGem, faCoins } from '@fortawesome/free-solid-svg-icons';

const StoreContainer = styled.div`
  padding: 4rem 8rem;
  max-width: 1800px;
  margin: 0 auto;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  
  @media (max-width: 1200px) {
    padding: 3rem 4rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  text-align: center;
  background: linear-gradient(45deg, var(--primary-color), rgba(255, 51, 51, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(255, 51, 51, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.25rem;
  }
`;

const PageSubtitle = styled.p`
  color: #aaa;
  text-align: center;
  max-width: 700px;
  margin: 1rem auto 3rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.6;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: var(--primary-color);
    margin: 20px auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const CategoryTab = styled.button`
  background-color: ${props => props.active ? 'var(--primary-color)' : 'rgba(10, 10, 10, 0.8)'};
  color: #fff;
  border: none;
  padding: 0.75rem 1.5rem;
  margin: 0.5rem;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.05)'};
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color)' : 'rgba(255, 51, 51, 0.1)'};
    border-color: var(--primary-color);
  }
  
  @media (max-width: 480px) {
    padding: 0.65rem 1rem;
    font-size: 0.9rem;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.div`
  background: rgba(10, 10, 10, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &:hover {
    transform: translateY(-5px);
    border-color: var(--primary-color);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 576px) {
    margin: 0 auto;
  }
`;

const ProductHeader = styled.div`
  background: linear-gradient(360deg, rgba(219, 7, 7, 0.95), var(--primary-color));
  padding: 1.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at top right, rgba(255, 51, 51, 0.1), transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ProductIcon = styled.div`
  font-size: 2rem;
  color: #fff;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const ProductTitle = styled.h3`
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  margin: 0;
  font-size: 1.1rem;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const ProductContent = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ProductDescription = styled.p`
  color: #aaa;
  font-family: 'Poppins', sans-serif;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }
`;

const ProductPrice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const Price = styled.span`
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 1.2rem;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const BuyButton = styled.a`
  display: block;
  background: var(--primary-color);
  color: #fff;
  text-align: center;
  padding: 0.75rem;
  border-radius: 6px;
  text-decoration: none;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  transition: transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    background: rgba(255, 51, 51, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 51, 51, 0.2);
  }
  
  @media (max-width: 480px) {
    padding: 0.65rem;
    font-size: 0.9rem;
  }
`;

const Store = () => {
  const [activeCategory, setActiveCategory] = useState('ranks');
  
  const categories = [
    { id: 'ranks', name: 'Ranks' },
    { id: 'crates', name: 'Crates' },
    { id: 'items', name: 'Items' },
    { id: 'cosmetics', name: 'Cosmetics' }
  ];
  
  const products = {
    ranks: [
      {
        title: 'VIP Rank',
        icon: faStar,
        description: 'Get access to exclusive VIP features including colored chat, /fly command, and 5 home locations.',
        price: '$9.99',
        link: '#'
      },
      {
        title: 'MVP Rank',
        icon: faCrown,
        description: 'All VIP features plus access to /heal, /feed, custom nicknames, and 10 home locations.',
        price: '$19.99',
        link: '#'
      },
      {
        title: 'LEGEND Rank',
        icon: faGem,
        description: 'Our highest rank with all features including item spawning, weather control, and unlimited homes.',
        price: '$49.99',
        link: '#'
      }
    ],
    crates: [
      {
        title: 'Common Crate',
        icon: faShoppingCart,
        description: 'Contains basic items and resources with a small chance of rare items.',
        price: '$4.99',
        link: '#'
      },
      {
        title: 'Rare Crate',
        icon: faShoppingCart,
        description: 'Contains resources and equipment with a good chance of rare items.',
        price: '$9.99',
        link: '#'
      },
      {
        title: 'Legendary Crate',
        icon: faShoppingCart,
        description: 'Contains the best items and exclusive cosmetics only available in this crate.',
        price: '$24.99',
        link: '#'
      }
    ],
    items: [
      {
        title: 'Resource Bundle',
        icon: faCoins,
        description: 'A bundle of essential resources to kickstart your adventure.',
        price: '$4.99',
        link: '#'
      },
      {
        title: 'Tool Kit',
        icon: faCoins,
        description: 'A complete set of diamond tools with enchantments.',
        price: '$7.99',
        link: '#'
      },
      {
        title: 'Armor Set',
        icon: faCoins,
        description: 'Full set of enchanted netherite armor for maximum protection.',
        price: '$12.99',
        link: '#'
      }
    ],
    cosmetics: [
      {
        title: 'Particle Effects',
        icon: faGem,
        description: 'Show off with custom particle effects that follow you as you move.',
        price: '$5.99',
        link: '#'
      },
      {
        title: 'Pet Companions',
        icon: faGem,
        description: 'Cute pet companions that follow you around the server.',
        price: '$8.99',
        link: '#'
      },
      {
        title: 'Custom Trails',
        icon: faGem,
        description: 'Leave a trail of particles behind you as you explore the world.',
        price: '$6.99',
        link: '#'
      }
    ]
  };
  
  return (
    <StoreContainer>
      <PageTitle>SERVER STORE</PageTitle>
      <PageSubtitle>
        Support our server by purchasing ranks, crates, and other items. All proceeds go towards server maintenance and future updates.
      </PageSubtitle>
      
      <CategoryTabs>
        {categories.map(category => (
          <CategoryTab 
            key={category.id}
            active={activeCategory === category.id}
            onClick={() => setActiveCategory(category.id)}
          >
            {category.name}
          </CategoryTab>
        ))}
      </CategoryTabs>
      
      <ProductGrid>
        {products[activeCategory].map((product, index) => (
          <ProductCard key={index}>
            <ProductHeader>
              <ProductIcon>
                <FontAwesomeIcon icon={product.icon} />
              </ProductIcon>
              <ProductTitle>{product.title}</ProductTitle>
            </ProductHeader>
            <ProductContent>
              <ProductDescription>{product.description}</ProductDescription>
              <ProductPrice>
                <Price>{product.price}</Price>
              </ProductPrice>
              <BuyButton href={product.link} target="_blank" rel="noopener noreferrer">
                Purchase Now
              </BuyButton>
            </ProductContent>
          </ProductCard>
        ))}
      </ProductGrid>
    </StoreContainer>
  );
};

export default Store;
